import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { FileText, Upload, X, Download, Users, ChevronRight, FolderOpen, Tag } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const DOC_TYPES = [
  { key: 'bilan', label: 'Bilans', color: '#E0F2F0', text: '#2E665E' },
  { key: 'compte_rendu', label: 'Comptes rendus', color: '#DBEAFE', text: '#1E3A8A' },
  { key: 'ordonnance', label: 'Ordonnances', color: '#FEF3C7', text: '#92400E' },
  { key: 'cerfa', label: 'Cerfa / MDPH', color: '#FEE2D4', text: '#9A3412' },
  { key: 'certificat', label: 'Certificats médicaux', color: '#FCE7F3', text: '#9D174D' },
  { key: 'scolaire', label: 'Documents scolaires', color: '#EDE9FE', text: '#5B21B6' },
  { key: 'administratif', label: 'Pièces administratives', color: '#F3E8FF', text: '#6B21A8' },
  { key: 'decision_mdph', label: 'Décisions MDPH', color: '#FEE2D4', text: '#9A3412' },
  { key: 'autre', label: 'Autres', color: '#F5F5F4', text: '#57534E' },
];

const getTypeInfo = (key) => DOC_TYPES.find(t => t.key === key) || DOC_TYPES[DOC_TYPES.length - 1];

export default function ParentDocuments() {
  const { user } = useAuth();
  const [childId, setChildId] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [linkedPros, setLinkedPros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vue, setVue] = useState('pro'); // 'pro' | 'type'
  const [selectedPro, setSelectedPro] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [filterType, setFilterType] = useState('tous');
  const [filterPro, setFilterPro] = useState('tous');
  const [showUpload, setShowUpload] = useState(false);
  const [uploadData, setUploadData] = useState({ title: '', category: 'autre', file: null });
  const [uploading, setUploading] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [docForAccess, setDocForAccess] = useState(null);
  const [selectedPros, setSelectedPros] = useState([]);
  const [docAccess, setDocAccess] = useState({});
  const [savingAccess, setSavingAccess] = useState(false);
  const [deletingDoc, setDeletingDoc] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { if (user) loadData(); }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data: link } = await supabase.from('parent_child_links').select('child_id').eq('parent_id', user.id).maybeSingle();
      if (!link?.child_id) return;
      setChildId(link.child_id);

      const { data: docs } = await supabase.from('documents').select('*').eq('child_id', link.child_id).order('created_at', { ascending: false });
      setDocuments(docs || []);

      const { data: pros } = await supabase.from('child_professionals').select('*').eq('child_id', link.child_id);
      setLinkedPros(pros || []);

      if (docs?.length > 0) {
        const { data: access } = await supabase.from('document_access').select('*').in('document_id', docs.map(d => d.id));
        const map = {};
        (access || []).forEach(a => {
          if (!map[a.document_id]) map[a.document_id] = [];
          map[a.document_id].push(a.professional_id || a.professional_name);
        });
        setDocAccess(map);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadData.title || !uploadData.file) return;
    setUploading(true);
    try {
      const ext = uploadData.file.name.split('.').pop();
      const fileName = `doc-${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage.from('documents').upload(fileName, uploadData.file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from('documents').getPublicUrl(fileName);
      await supabase.from('documents').insert({
        child_id: childId,
        title: uploadData.title,
        type: uploadData.category,
        category: uploadData.category,
        file_url: urlData.publicUrl,
        file_name: uploadData.file.name,
        uploaded_by_id: user.id,
        uploaded_by_type: 'parent',
        created_at: new Date().toISOString(),
      });
      setShowUpload(false);
      setUploadData({ title: '', category: 'autre', file: null });
      loadData();
    } catch (e) {
      console.error(e);
    } finally {
      setUploading(false);
    }
  };

  const saveAccess = async () => {
    if (!docForAccess) return;
    setSavingAccess(true);
    try {
      await supabase.from('document_access').delete().eq('document_id', docForAccess.id);
      if (selectedPros.length > 0) {
        await supabase.from('document_access').insert(
          selectedPros.map(proId => {
            const pro = linkedPros.find(p => p.id === proId);
            return { document_id: docForAccess.id, professional_id: pro?.professional_id || null, professional_name: pro?.professional_name, granted_by: user.id };
          })
        );
      }
      setShowAccessModal(false);
      setDocForAccess(null);
      setSelectedPros([]);
      loadData();
    } finally {
      setSavingAccess(false);
    }
  };

  const deleteDocument = async (doc) => {
    if (!window.confirm(`Supprimer "${doc.title}" ?`)) return;
    setDeletingDoc(doc.id);
    try {
      await supabase.from('documents').delete().eq('id', doc.id);
      loadData();
    } finally {
      setDeletingDoc(null);
    }
  };

  // --- Calculs pour les dossiers ---
  const getDocsForPro = (pro) => {
    return documents.filter(d =>
      d.professional_id === pro.professional_id ||
      (docAccess[d.id] || []).includes(pro.professional_id || pro.professional_name)
    );
  };

  const getDocsForType = (typeKey) => documents.filter(d => (d.category || d.type) === typeKey);

  const getFilteredDocs = () => {
    let docs = documents;
    if (filterType !== 'tous') docs = docs.filter(d => (d.category || d.type) === filterType);
    if (filterPro !== 'tous') {
      const pro = linkedPros.find(p => p.id === filterPro);
      if (pro) docs = docs.filter(d =>
        d.professional_id === pro.professional_id ||
        (docAccess[d.id] || []).includes(pro.professional_id || pro.professional_name)
      );
    }
    return docs;
  };

  if (loading) return (
    <div className="p-5 space-y-3">
      {[1,2,3].map(i => <div key={i} className="h-20 bg-stone-100 rounded-3xl animate-pulse" />)}
    </div>
  );

  // Vue détail dossier pro
  if (selectedPro) {
    const docs = getDocsForPro(selectedPro);
    return <FolderDetail
      title={selectedPro.professional_name}
      subtitle={selectedPro.profession}
      docs={docs}
      docAccess={docAccess}
      linkedPros={linkedPros}
      onBack={() => setSelectedPro(null)}
      onDelete={deleteDocument}
      deletingDoc={deletingDoc}
      onAccess={(doc) => { setDocForAccess(doc); setSelectedPros(linkedPros.filter(p => (docAccess[doc.id]||[]).includes(p.professional_id||p.professional_name)).map(p=>p.id)); setShowAccessModal(true); }}
      showAccessModal={showAccessModal}
      docForAccess={docForAccess}
      setShowAccessModal={setShowAccessModal}
      selectedPros={selectedPros}
      setSelectedPros={setSelectedPros}
      saveAccess={saveAccess}
      savingAccess={savingAccess}
    />;
  }

  // Vue détail dossier type
  if (selectedType) {
    const typeInfo = getTypeInfo(selectedType);
    const docs = getDocsForType(selectedType);
    return <FolderDetail
      title={typeInfo.label}
      subtitle={`${docs.length} document${docs.length > 1 ? 's' : ''}`}
      docs={docs}
      docAccess={docAccess}
      linkedPros={linkedPros}
      onBack={() => setSelectedType(null)}
      onDelete={deleteDocument}
      deletingDoc={deletingDoc}
      onAccess={(doc) => { setDocForAccess(doc); setSelectedPros(linkedPros.filter(p => (docAccess[doc.id]||[]).includes(p.professional_id||p.professional_name)).map(p=>p.id)); setShowAccessModal(true); }}
      showAccessModal={showAccessModal}
      docForAccess={docForAccess}
      setShowAccessModal={setShowAccessModal}
      selectedPros={selectedPros}
      setSelectedPros={setSelectedPros}
      saveAccess={saveAccess}
      savingAccess={savingAccess}
    />;
  }

  return (
    <div className="px-5 pt-5 pb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="page-title">Documents</h1>
          <p className="text-sm text-slate-400 font-body mt-0.5">{documents.length} document{documents.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => setShowUpload(true)}
          className="w-10 h-10 rounded-2xl flex items-center justify-center shadow-card"
          style={{ backgroundColor: '#4A9B8F' }}>
          <Upload size={18} color="white" />
        </button>
      </div>

      {/* Toggle vue */}
      <div className="flex gap-2 mb-5 bg-stone-100 p-1 rounded-2xl">
        <button onClick={() => setVue('pro')}
          className={`flex-1 py-2 rounded-xl text-sm font-heading font-semibold transition-all flex items-center justify-center gap-1.5 ${vue === 'pro' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400'}`}>
          <Users size={14} /> Par professionnel
        </button>
        <button onClick={() => setVue('type')}
          className={`flex-1 py-2 rounded-xl text-sm font-heading font-semibold transition-all flex items-center justify-center gap-1.5 ${vue === 'type' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400'}`}>
          <Tag size={14} /> Par type
        </button>
      </div>

      {vue === 'pro' ? (
        <div className="space-y-3">
          {/* Dossier "Non classés" */}
          {(() => {
            const nonClasses = documents.filter(d => !d.professional_id && Object.keys(docAccess).every(id => id !== d.id || (docAccess[id] || []).length === 0));
            return (
              <>
                {linkedPros.length === 0 && (
                  <div className="passerelle-card text-center py-6">
                    <p className="text-sm text-slate-400 font-body">Aucun professionnel dans l'équipe pour le moment.</p>
                  </div>
                )}
                {linkedPros.map(pro => {
                  const docs = getDocsForPro(pro);
                  return (
                    <button key={pro.id} onClick={() => setSelectedPro(pro)}
                      className="passerelle-card w-full flex items-center gap-4 text-left">
                      <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-heading font-bold text-lg flex-shrink-0"
                        style={{ backgroundColor: '#4A9B8F' }}>
                        {pro.professional_name?.[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-heading font-semibold text-sm text-slate-800">{pro.professional_name}</p>
                        <p className="text-xs text-slate-400 font-body">{pro.profession}</p>
                        <p className="text-xs text-slate-400 font-body mt-0.5">{docs.length} document{docs.length !== 1 ? 's' : ''}</p>
                      </div>
                      <ChevronRight size={16} className="text-slate-300 flex-shrink-0" />
                    </button>
                  );
                })}
                {nonClasses.length > 0 && (
                  <button onClick={() => setSelectedPro({ professional_name: 'Non classés', profession: '', professional_id: '__nonclasses__' })}
                    className="passerelle-card w-full flex items-center gap-4 text-left">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 bg-stone-100">
                      <FolderOpen size={20} className="text-slate-400" />
                    </div>
                    <div className="flex-1">
                      <p className="font-heading font-semibold text-sm text-slate-800">Non classés</p>
                      <p className="text-xs text-slate-400 font-body mt-0.5">{nonClasses.length} document{nonClasses.length !== 1 ? 's' : ''}</p>
                    </div>
                    <ChevronRight size={16} className="text-slate-300 flex-shrink-0" />
                  </button>
                )}
              </>
            );
          })()}
        </div>
      ) : (
        <div className="space-y-3">
          {DOC_TYPES.map(type => {
            const docs = getDocsForType(type.key);
            if (docs.length === 0) return null;
            return (
              <button key={type.key} onClick={() => setSelectedType(type.key)}
                className="passerelle-card w-full flex items-center gap-4 text-left">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: type.color }}>
                  <FileText size={20} style={{ color: type.text }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-semibold text-sm text-slate-800">{type.label}</p>
                  <p className="text-xs text-slate-400 font-body mt-0.5">{docs.length} document{docs.length !== 1 ? 's' : ''}</p>
                </div>
                <ChevronRight size={16} className="text-slate-300 flex-shrink-0" />
              </button>
            );
          })}
          {DOC_TYPES.every(t => getDocsForType(t.key).length === 0) && (
            <div className="passerelle-card text-center py-8">
              <FileText size={32} className="text-slate-200 mx-auto mb-2" />
              <p className="text-sm text-slate-400 font-body">Aucun document pour le moment</p>
            </div>
          )}
        </div>
      )}

      {/* Modal upload */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-[480px] bg-white rounded-3xl p-6 overflow-y-auto max-h-[85vh]">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading font-bold text-lg text-slate-800">Déposer un document</h3>
              <button onClick={() => setShowUpload(false)} className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center">
                <X size={16} className="text-slate-500" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-heading font-bold uppercase tracking-widest text-slate-400 mb-1.5">Titre *</label>
                <input type="text" value={uploadData.title} onChange={e => setUploadData(p => ({ ...p, title: e.target.value }))}
                  placeholder="Ex : Bilan orthophonique mars 2026"
                  className="w-full h-11 px-4 bg-stone-50 border border-stone-200 rounded-xl text-sm font-body" />
              </div>
              <div>
                <label className="block text-xs font-heading font-bold uppercase tracking-widest text-slate-400 mb-1.5">Type de document</label>
                <select value={uploadData.category} onChange={e => setUploadData(p => ({ ...p, category: e.target.value }))}
                  className="w-full h-11 px-4 bg-stone-50 border border-stone-200 rounded-xl text-sm font-body">
                  {DOC_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-heading font-bold uppercase tracking-widest text-slate-400 mb-1.5">Fichier *</label>
                <input ref={fileRef} type="file" className="hidden" onChange={e => setUploadData(p => ({ ...p, file: e.target.files[0] }))} />
                {uploadData.file ? (
                  <div className="flex items-center gap-2 p-3 bg-sage-50 rounded-xl border border-sage-200">
                    <FileText size={16} className="text-sage-600" />
                    <span className="text-sm font-body text-sage-700 flex-1 truncate">{uploadData.file.name}</span>
                    <button onClick={() => setUploadData(p => ({ ...p, file: null }))}><X size={14} className="text-slate-400" /></button>
                  </div>
                ) : (
                  <button onClick={() => fileRef.current?.click()}
                    className="w-full py-4 border-2 border-dashed border-stone-200 rounded-xl flex flex-col items-center gap-2 text-slate-400">
                    <Upload size={20} />
                    <span className="text-sm font-heading font-semibold">Sélectionner un fichier</span>
                  </button>
                )}
              </div>
            </div>
            <button onClick={handleUpload} disabled={uploading || !uploadData.title || !uploadData.file}
              className="w-full mt-5 h-12 rounded-2xl font-heading font-semibold disabled:opacity-40 flex items-center justify-center gap-2"
              style={{ backgroundColor: '#4A9B8F', color: 'white' }}>
              {uploading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Upload size={16} />}
              Déposer le document
            </button>
          </div>
        </div>
      )}

      <AccessModal
        show={showAccessModal}
        doc={docForAccess}
        linkedPros={linkedPros}
        selectedPros={selectedPros}
        setSelectedPros={setSelectedPros}
        onClose={() => { setShowAccessModal(false); setDocForAccess(null); }}
        onSave={saveAccess}
        saving={savingAccess}
      />
    </div>
  );
}

// --- Composant vue détail d'un dossier ---
function FolderDetail({ title, subtitle, docs, docAccess, linkedPros, onBack, onDelete, deletingDoc, onAccess, showAccessModal, docForAccess, setShowAccessModal, selectedPros, setSelectedPros, saveAccess, savingAccess }) {
  return (
    <div className="px-5 pt-5 pb-6">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-400 font-heading font-semibold mb-5">
        ← Retour
      </button>
      <div className="mb-5">
        <h1 className="page-title">{title}</h1>
        <p className="text-sm text-slate-400 font-body mt-0.5">{subtitle}</p>
      </div>

      {docs.length === 0 ? (
        <div className="passerelle-card text-center py-8">
          <FileText size={32} className="text-slate-200 mx-auto mb-2" />
          <p className="text-sm text-slate-400 font-body">Aucun document dans ce dossier</p>
        </div>
      ) : (
        <div className="space-y-3">
          {docs.map(doc => {
            const typeInfo = getTypeInfo(doc.category || doc.type);
            const date = doc.created_at ? format(new Date(doc.created_at), 'd MMM yyyy', { locale: fr }) : '';
            const accessCount = (docAccess[doc.id] || []).length;
            return (
              <div key={doc.id} className="passerelle-card flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: typeInfo.color }}>
                  <FileText size={20} style={{ color: typeInfo.text }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-semibold text-sm text-slate-800 leading-tight">{doc.title}</p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className="text-[11px] font-heading font-semibold px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: typeInfo.color, color: typeInfo.text }}>
                      {typeInfo.label}
                    </span>
                    <span className="text-xs text-slate-400 font-body">{date}</span>
                    {accessCount > 0 && (
                      <span className="text-[11px] font-heading font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-500">
                        {accessCount} pro{accessCount > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {doc.file_url && (
                    <a href={doc.file_url} target="_blank" rel="noreferrer"
                      className="w-9 h-9 rounded-xl bg-sage-50 flex items-center justify-center text-sage-600">
                      <Download size={16} />
                    </a>
                  )}
                  <button onClick={() => onAccess(doc)}
                    className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center text-blue-500">
                    <Users size={14} />
                  </button>
                  <button onClick={() => onDelete(doc)} disabled={deletingDoc === doc.id}
                    className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center text-red-400">
                    <X size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AccessModal
        show={showAccessModal}
        doc={docForAccess}
        linkedPros={linkedPros}
        selectedPros={selectedPros}
        setSelectedPros={setSelectedPros}
        onClose={() => setShowAccessModal(false)}
        onSave={saveAccess}
        saving={savingAccess}
      />
    </div>
  );
}

// --- Modal accès pros ---
function AccessModal({ show, doc, linkedPros, selectedPros, setSelectedPros, onClose, onSave, saving }) {
  if (!show || !doc) return null;
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-[480px] bg-white rounded-3xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg text-slate-800">Autoriser l'accès</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center">
            <X size={16} className="text-slate-500" />
          </button>
        </div>
        <p className="text-sm text-slate-500 font-body mb-4 truncate">{doc.title}</p>
        {linkedPros.length === 0 ? (
          <p className="text-sm text-slate-400 font-body text-center py-4">Aucun professionnel dans l'équipe</p>
        ) : (
          <div className="space-y-2 mb-5">
            {linkedPros.map(pro => (
              <button key={pro.id}
                onClick={() => setSelectedPros(prev => prev.includes(pro.id) ? prev.filter(id => id !== pro.id) : [...prev, pro.id])}
                className={`w-full p-3 rounded-2xl border-2 text-left flex items-center gap-3 transition-all ${selectedPros.includes(pro.id) ? 'border-sage-400 bg-sage-50' : 'border-stone-200 bg-white'}`}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-heading font-bold text-sm flex-shrink-0"
                  style={{ backgroundColor: '#4A9B8F' }}>
                  {pro.professional_name?.[0]}
                </div>
                <div className="flex-1">
                  <p className="font-heading font-semibold text-sm text-slate-700">{pro.professional_name}</p>
                  <p className="text-xs text-slate-400 font-body">{pro.profession}</p>
                </div>
                {selectedPros.includes(pro.id) && (
                  <div className="w-5 h-5 rounded-full bg-sage-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs">✓</span>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 h-11 bg-stone-100 text-slate-600 rounded-2xl font-heading font-semibold text-sm">Annuler</button>
          <button onClick={onSave} disabled={saving}
            className="flex-1 h-11 rounded-2xl font-heading font-semibold text-sm disabled:opacity-40"
            style={{ backgroundColor: '#4A9B8F', color: 'white' }}>
            {saving ? '...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}